import React from "react";
import { Box, Link, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import { TEXT } from "../../dark-theme/palette";
import { usePagesTranslation } from "../locales";
import LatestStoriesLinks from "./LatestStoriesLinks";

const COPY = {
    CROSSWORD: {
        titleKey: "puzzles-browse.about-title-crossword",
        titleFallback: "About these crossword puzzles",
        bodyKey: "puzzles-browse.about-body-crossword",
        bodyFallback:
            "This library is a set of original story crosswords from Puzzle Interact. Each puzzle is written with an illustrated tale, so clues and theme words come from the same vocabulary — not a random word list. Browse by topic, open a card, and play in the browser; a PDF is available when you want to print. New puzzles are added as we publish stories.",
        moreKey: "puzzles-browse.about-more-crossword",
        moreFallback:
            "Read the full illustrated story on its own page, then come back here to play. We publish new original stories rather than recycling public-domain word lists.",
        howtoPath: "/howto/puzzle/crossword",
        howtoKey: "puzzles-browse.about-howto-crossword",
        howtoFallback: "How to play crossword",
    },
    WORD_SEARCH: {
        titleKey: "puzzles-browse.about-title-word-search",
        titleFallback: "About these word search puzzles",
        bodyKey: "puzzles-browse.about-body-word-search",
        bodyFallback:
            "These word searches are built from our illustrated stories. The word list is the vocabulary of that tale, so finding each word is also reading practice. Grids are published with topic tags so you can pick nature, science, or everyday themes. Play free puzzles here, or open How to Play from the site menu for direction examples and tips.",
        moreKey: "puzzles-browse.about-more-word-search",
        moreFallback:
            "Every grid here belongs to an original Puzzle Interact story. Open a recent story below to read the text, then play the matching search.",
        howtoPath: "/howto/puzzle/word-search",
        howtoKey: "puzzles-browse.about-howto-word-search",
        howtoFallback: "How to play word search",
    },
    HIDDEN_MESSAGE: {
        titleKey: "puzzles-browse.about-title-hidden-message",
        titleFallback: "About these hidden message puzzles",
        bodyKey: "puzzles-browse.about-body-hidden-message",
        bodyFallback:
            "Hidden Message is our word-search format with a leftover quote. After you find the listed words, unused letters spell a line that matches the story. Every puzzle here is an original Puzzle Interact piece with an illustration and a word list, not a blank arcade screen. Use the How to Play pages in the menu for a worked example.",
        moreKey: "puzzles-browse.about-more-hidden-message",
        moreFallback:
            "The leftover quote is part of the same original story as the word list. Read a recent tale below, then solve the grid.",
        howtoPath: "/howto/puzzle/hidden-message",
        howtoKey: "puzzles-browse.about-howto-hidden-message",
        howtoFallback: "How to play hidden message",
    },
    SLIDING: {
        titleKey: "puzzles-browse.about-title-sliding",
        titleFallback: "About these sliding puzzles",
        bodyKey: "puzzles-browse.about-body-sliding",
        bodyFallback:
            "Each sliding puzzle starts from a story photograph or illustration cut into tiles, with one empty cell. Choose a story below, then pick a grid size and difficulty on the next screen. The same pictures appear in Sliding Puzzle Quest on mobile. This page is the public catalog — stories, tags, and Top 5 — not an empty board. Controls and move examples live under How to Play in the site menu.",
        moreKey: "puzzles-browse.about-more-sliding",
        moreFallback:
            "The pictures are original Puzzle Interact illustrations tied to a written story. Browse recent stories below, then open a sliding board from the catalog.",
        howtoPath: "/howto/puzzle/sliding",
        howtoKey: "puzzles-browse.about-howto-sliding",
        howtoFallback: "How to play sliding puzzles",
    },
    JIGSAW: {
        titleKey: "puzzles-browse.about-title-jigsaw",
        titleFallback: "About these jigsaw puzzles",
        bodyKey: "puzzles-browse.about-body-jigsaw",
        bodyFallback:
            "Jigsaw Muse on the web is a catalog of original illustrated jigsaws. Open a story, choose a piece count, then assemble on the board. Larger sizes can use color baskets to sort pieces before you place them. Listings show the picture, tags, and whether a puzzle is free or Standard. For piece controls and color-basket steps, use How to Play in the menu rather than a second tutorial on this page.",
        moreKey: "puzzles-browse.about-more-jigsaw",
        moreFallback:
            "Each jigsaw starts from an original illustrated story, not a stock photo dump. Read a recent story below, then pick a piece count from the catalog.",
        howtoPath: "/howto/puzzle/jigsaw",
        howtoKey: "puzzles-browse.about-howto-jigsaw",
        howtoFallback: "How to play jigsaw",
    },
};

/**
 * Short catalog copy for AdSense/search quality. Points at the existing How to Play
 * menu instead of repeating those tutorials.
 */
export default function PuzzleListingAbout({ puzzleKind, flush = false }) {
    const { t } = usePagesTranslation();
    const copy = COPY[puzzleKind];
    if (!copy) return null;
    const fullWidth = puzzleKind === "JIGSAW" || puzzleKind === "SLIDING";

    return (
        <Box
            component="section"
            aria-labelledby={`puzzle-listing-about-${puzzleKind}`}
            sx={{
                mt: flush ? 0 : { xs: 4, sm: 5 },
                pt: flush ? 0 : { xs: 3, sm: 3.5 },
                maxWidth: flush || fullWidth ? "100%" : 720,
                width: "100%",
                borderTop: flush ? "none" : `1px solid ${TEXT.disabled}33`,
            }}
        >
            <Typography
                id={`puzzle-listing-about-${puzzleKind}`}
                variant="h6"
                component="h2"
                sx={{ fontWeight: 700, mb: 1.25, color: TEXT.primary }}
            >
                {t(copy.titleKey, copy.titleFallback)}
            </Typography>
            <Typography
                variant="body1"
                component="p"
                sx={{ mb: 1.5, lineHeight: 1.7, color: TEXT.secondary, fontSize: { xs: "0.95rem", sm: "1.05rem" } }}
            >
                {t(copy.bodyKey, copy.bodyFallback)}
            </Typography>
            {copy.moreKey ? (
                <Typography
                    variant="body1"
                    component="p"
                    sx={{ mb: 1.5, lineHeight: 1.7, color: TEXT.secondary, fontSize: { xs: "0.95rem", sm: "1.05rem" } }}
                >
                    {t(copy.moreKey, copy.moreFallback)}
                </Typography>
            ) : null}
            <Typography variant="body2" sx={{ color: TEXT.secondary }}>
                <Link
                    component={RouterLink}
                    to={copy.howtoPath}
                    sx={{ fontWeight: 600, color: "inherit", textDecoration: "underline" }}
                >
                    {t(copy.howtoKey, copy.howtoFallback)}
                </Link>
            </Typography>
            <LatestStoriesLinks puzzleKind={puzzleKind} />
        </Box>
    );
}

PuzzleListingAbout.propTypes = {
    puzzleKind: PropTypes.oneOf([
        "CROSSWORD",
        "WORD_SEARCH",
        "HIDDEN_MESSAGE",
        "SLIDING",
        "JIGSAW",
    ]).isRequired,
    flush: PropTypes.bool,
};
